import {
  Avatar,
  Box,
  createStyles,
  Drawer,
  Flex,
  Group,
  Progress,
  Text,
  Badge,
} from "@mantine/core";
import React from "react";
import { IconUsers } from "@tabler/icons-react";

const useStyles = createStyles(() => ({
  drawer: {
    ".mantine-Drawer-content": {
      backgroundColor: "var(--app-bg)",
    },
    ".mantine-Drawer-title": {
      fontSize: 20,
      fontWeight: 700,
      fontFamily: "Inter",
      color: "var(--app-text)",
      width: "100%",
    },
    ".mantine-Drawer-header": {
      backgroundColor: "var(--app-surface)",
    },
  },
  container: {
    backgroundColor: "var(--app-surface)",
    borderRadius: "10px",
    padding: "16px",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: "var(--app-border)",
    "::-webkit-scrollbar": { width: "10px" },
    "::-webkit-scrollbar-track": { background: "#f0f0f0", borderRadius: "10px" },
    "::-webkit-scrollbar-thumb": { background: "#888", borderRadius: "10px" },
    "::-webkit-scrollbar-thumb:hover": { background: "var(--app-border)" },
  },
  staffRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 12,
    padding: "16px 0",
    borderBottom: "1px solid var(--app-border)",
    "&:last-child": { borderBottom: "none" },
  },
  avatar: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    backgroundColor: "var(--app-tint-blue)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    fontSize: 14,
    fontWeight: 600,
    color: "#3B82F6",
    fontFamily: "Inter",
  },
  statBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 12,
    fontSize: 11,
    fontWeight: 600,
    fontFamily: "Inter",
  },
}));

const StaffTasksDrawer = ({ opened, close, staffSummary }) => {
  const { classes } = useStyles();

  const totalTasks = staffSummary?.reduce((sum, s) => sum + (s.weeklyStats?.total || 0), 0) || 0;
  const totalCompleted = staffSummary?.reduce((sum, s) => sum + (s.weeklyStats?.completed || 0), 0) || 0;
  const totalInProgress = staffSummary?.reduce((sum, s) => sum + (s.weeklyStats?.inProgress || 0), 0) || 0;
  const totalPending = totalTasks - totalCompleted - totalInProgress;

  return (
    <Drawer
      size="35%"
      offset={8}
      radius="md"
      zIndex={1000}
      position="right"
      title={
        <Group spacing={10}>
          <IconUsers size={22} color="#3B82F6" />
          <Text ff="Inter" fw={700} fz={18} color="var(--app-text)">
            Staff Tasks ({staffSummary?.length || 0} staff)
          </Text>
        </Group>
      }
      opened={opened}
      className={classes.drawer}
      onClose={close}
      overlayProps={{ backgroundOpacity: 2.5, blur: 4 }}
    >
      {/* Summary bar */}
      <Box px={16} py={12} mb={8}>
        <Text ff="Inter" fz={12} fw={600} color="var(--app-text-muted)" mb={6} style={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
          This Week
        </Text>
        <Flex gap={16} wrap="wrap">
          <Flex align="center" gap={6}>
            <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3B82F6" }} />
            <Text ff="Inter" fz={13} color="var(--app-text-muted)">{totalTasks} total</Text>
          </Flex>
          <Flex align="center" gap={6}>
            <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10B981" }} />
            <Text ff="Inter" fz={13} color="var(--app-text-muted)">{totalCompleted} completed</Text>
          </Flex>
          {totalInProgress > 0 && (
            <Flex align="center" gap={6}>
              <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#3B82F6" }} />
              <Text ff="Inter" fz={13} color="var(--app-text-muted)">{totalInProgress} in progress</Text>
            </Flex>
          )}
          {totalPending > 0 && (
            <Flex align="center" gap={6}>
              <Box style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#F59E0B" }} />
              <Text ff="Inter" fz={13} color="#F59E0B" fw={500}>{totalPending} pending</Text>
            </Flex>
          )}
        </Flex>
      </Box>

      <Box className={classes.container}>
        {staffSummary?.length > 0 ? (
          staffSummary.map((staff) => {
            const ws = staff.weeklyStats || {};
            const weekTotal = ws.total || 0;
            const weekCompleted = ws.completed || 0;
            const weekInProgress = ws.inProgress || 0;
            const weekPending = ws.pending || 0;
            const pct = weekTotal > 0 ? Math.round((weekCompleted / weekTotal) * 100) : 0;
            const barColor = weekTotal === 0 ? "gray" : pct === 100 ? "green" : pct >= 50 ? "teal" : pct >= 25 ? "yellow" : "orange";

            return (
              <Box key={staff.id} className={classes.staffRow}>
                {/* Avatar with login indicator */}
                <Box style={{ position: "relative", flexShrink: 0 }}>
                  <Avatar
                    size={38}
                    radius="xl"
                    src={staff.photo || null}
                    alt={staff.name}
                    color="blue"
                    styles={{
                      root: { flexShrink: 0 },
                      placeholder: { backgroundColor: "var(--app-tint-blue)", color: "#3B82F6", fontFamily: "Inter", fontSize: 14, fontWeight: 600 },
                    }}
                  >
                    {staff.name?.charAt(0)?.toUpperCase() || "?"}
                  </Avatar>
                  <Box style={{
                    position: "absolute", bottom: -1, right: -1,
                    width: 10, height: 10, borderRadius: "50%",
                    backgroundColor: staff.loggedIn ? "#10B981" : "#D1D5DB",
                    border: "2px solid #fff",
                  }} />
                </Box>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Flex align="center" gap={6} style={{ flex: 1 }}>
                      <Text ff="Inter" fz={14} fw={600} color="var(--app-text)" lineClamp={1}>
                        {staff.name}
                      </Text>
                      {staff.position && (
                        <Text ff="Inter" fz={11} color="var(--app-text-muted)" lineClamp={1}>· {staff.position}</Text>
                      )}
                      {!staff.loggedIn && (
                        <Badge variant="outline" color="gray" size="xs" radius="sm" ff="Inter"
                          styles={{ root: { textTransform: "none", fontWeight: 500, borderColor: "var(--app-border)" } }}>
                          Offline
                        </Badge>
                      )}
                    </Flex>
                    {weekTotal > 0 ? (
                      <Text ff="Inter" fz={14} fw={700} color={pct === 100 ? "#10B981" : "var(--app-text)"} ml={8}>
                        {weekCompleted}/{weekTotal}
                      </Text>
                    ) : staff.total > 0 ? (
                      <Text ff="Inter" fz={12} fw={500} color="#F59E0B" ml={8}>No task completed</Text>
                    ) : (
                      <Text ff="Inter" fz={12} color="var(--app-text-muted)" ml={8}>No tasks assigned</Text>
                    )}
                  </Flex>
                  {weekTotal > 0 && (
                    <Progress value={pct} color={barColor} size="sm" radius="xl" />
                  )}
                  <Flex gap={8} mt={8} wrap="wrap">
                    {weekCompleted > 0 && (
                      <Badge variant="light" color="green" size="xs" radius="sm" ff="Inter">
                        {weekCompleted} completed
                      </Badge>
                    )}
                    {weekInProgress > 0 && (
                      <Badge variant="light" color="blue" size="xs" radius="sm" ff="Inter">
                        {weekInProgress} in progress
                      </Badge>
                    )}
                    {weekPending > 0 && (
                      <Badge variant="light" color="yellow" size="xs" radius="sm" ff="Inter">
                        {weekPending} pending
                      </Badge>
                    )}
                    {staff.missedDaysCount > 0 && (
                      <Badge variant="light" color="red" size="xs" radius="sm" ff="Inter">
                        {staff.missedDaysCount} missed days
                      </Badge>
                    )}
                  </Flex>
                </Box>
              </Box>
            );
          })
        ) : (
          <Flex align="center" justify="center" py={60}>
            <Text ff="Inter" fz={14} color="var(--app-text-muted)">No staff tasks assigned</Text>
          </Flex>
        )}
      </Box>
    </Drawer>
  );
};

export default StaffTasksDrawer;
